import axiosMain, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { CODES } from "../types/enums/error-codes";
import { useRefreshToken } from "../api/AuthApi";
import { TokenHelper } from "../utils/tokensHelper";
import { ApiModules } from "@/types/enums/api-modules";

// Define base URLs for different modules
const BASE_URLS: Record<ApiModules, string> = {
  [ApiModules.AUTH]: "https://dev.pheoc.cm/",
  [ApiModules.PROJET]: "https://dev.pheoc.cm/",
  [ApiModules.FORUM]: "https://dev.pheoc.cm/",
  [ApiModules.RESOURCES_PUBLICATION]: "https://dev.pheoc.cm/",
  [ApiModules.MENTORAT]: "https://dev.pheoc.cm/",
};

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

// Modify `useAxios` to accept a module type
const useAxios = (module: ApiModules) => {
  const { refreshToken } = useRefreshToken();

  const axios = axiosMain.create({
    baseURL: BASE_URLS[module], // Dynamically set the base URL
  });

  axios.interceptors.request.use(
    (config) => {
      const tokenHelper = new TokenHelper();
      const { access } = tokenHelper.getTokens();

      // Explicitly skip Authorization header for auth routes
      const url = config.url ?? "";
      const isAuthRoute =
        url.includes("auth/token") ||
        url.includes("auth/register") ||
        url.includes("auth/2fa") ||
        url.includes("auth/password-reset") ||
        url.includes("auth/reset-password") ||
        url.includes("auth/verify-email") ||
        url.includes("auth/resend-verification") ||
        url.includes("auth/google");

      if (!isAuthRoute && access) {
        config.headers.Authorization = `Bearer ${access}`;
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  axios.interceptors.response.use(
    function (response: AxiosResponse): AxiosResponse {
      return response;
    },
    async function (
      error: AxiosError<Record<string, unknown>>,
    ): Promise<never> {
      const code = error.response?.data?.code;
      const originalRequest = error.config as CustomAxiosRequestConfig;

      // Handle expired token
      if (code === CODES.INVALID_OR_EXPIRED_TOKEN && !originalRequest._retry) {
        console.log("⏳ Token expired. Trying to refresh..."); // Log when retry starts
        originalRequest._retry = true; // Prevent infinite loops

        try {
          const response = await refreshToken(); // Fetch new token

          const newAccessToken = response?.access;
          if (newAccessToken) {
            console.log(
              "✅ Token refreshed. Retrying request:",
              originalRequest.url,
            );

            // ✅ Update request headers
            originalRequest.headers = {
              ...originalRequest.headers,
              Authorization: `Bearer ${newAccessToken}`,
            };

            return axios(originalRequest);
          }
        } catch (refreshError) {
          console.error("Token refresh failed", refreshError);
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    },
  );

  return { axios };
};

export default useAxios;
